import React, { useEffect, useRef, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import "../themeupdate/themeupdate.css";
import "./stagequiz.css";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { getQuizStages, saveQuizStages, saveQuizStageQuestions } from "../../functions/quizStages";
import { uploadFileDirectToS3 } from "../../functions/uploadFileDirectToS3";
import { useAdminTour } from "../../tour/useAdminTour";
import TourOverlay from "../../tour/TourOverlay";

const STAGE_TYPES = [
  {
    value: "image_audio",
    label: "Image + Audio",
    description: "Image question with an audio clip, 3 options",
    icon: "fa-solid fa-headphones-simple",
    optionCount: 3,
    needsImage: true,
    needsAudio: true,
    imageAccept: ".jpg,.jpeg,.png",
    imageExts: ["jpg", "jpeg", "png"],
    imageDimensions: [[582, 379]],
    // The image + audio together are the question — no separate text prompt.
    needsQuestionText: false,
    questionMaxLen: 300,
  },
  {
    value: "gif",
    label: "GIF Question",
    description: "Animated GIF as the question, 3 options",
    icon: "fa-solid fa-file-video",
    optionCount: 3,
    needsImage: true,
    needsAudio: false,
    imageAccept: ".gif",
    imageExts: ["gif"],
    imageDimensions: [[480, 240]],
    needsQuestionText: false,
    questionMaxLen: 300,
  },
  {
    value: "image3",
    label: "Image (3 options)",
    description: "Image question, 3 options",
    icon: "fa-solid fa-image",
    optionCount: 3,
    needsImage: true,
    needsAudio: false,
    imageAccept: ".jpg,.jpeg,.png",
    imageExts: ["jpg", "jpeg", "png"],
    imageDimensions: [[882, 158], [1043, 487], [582, 379]],
    needsQuestionText: false,
    questionMaxLen: 300,
  },
  {
    value: "image4",
    label: "Image (4 options)",
    description: "Image question, 4 options",
    icon: "fa-solid fa-images",
    optionCount: 4,
    needsImage: true,
    needsAudio: false,
    imageAccept: ".jpg,.jpeg,.png",
    imageExts: ["jpg", "jpeg", "png"],
    imageDimensions: [[582, 379], [1043, 487]],
    needsQuestionText: false,
    questionMaxLen: 300,
  },
  {
    value: "text3",
    label: "Text Question",
    description: "Text-only question, 3 options",
    icon: "fa-solid fa-align-left",
    optionCount: 3,
    needsImage: false,
    needsAudio: false,
    imageDimensions: [],
    needsQuestionText: true,
    questionMaxLen: 150,
  },
  {
    value: "audio4",
    label: "Audio Question",
    description: "Audio clip question, 4 options",
    icon: "fa-solid fa-headphones-simple",
    optionCount: 4,
    needsImage: false,
    needsAudio: true,
    imageDimensions: [],
    needsQuestionText: false,
    questionMaxLen: 300,
  },
];

const STAGE_TYPE_BY_VALUE = STAGE_TYPES.reduce((acc, t) => ({ ...acc, [t.value]: t }), {});

const MAX_TITLE_LEN = 80;
const MAX_HEADING_LEN = 100;
const MAX_OPTION_LEN = 100;
const MAX_IMAGE_MB = 5;
const MAX_AUDIO_MB = 10;
const AUDIO_EXTS = ["mp3", "wav", "ogg", "m4a", "aac"];
const MIN_QUESTION_CARDS = 2; // initial number of empty question slots seeded for a brand-new stage
const MAX_QUESTION_CARDS = 12;

const dimensionsLabel = (dims) => dims.map(([w, h]) => `${w}x${h}`).join(" or ");

const s3Url = (key) => (key ? `${process.env.REACT_APP_S3_PATH}${key}` : "");

function getImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.width, height: img.height });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not load image"));
    };
    img.src = url;
  });
}

function emptyQuestionCard(stageType) {
  const cfg = STAGE_TYPE_BY_VALUE[stageType];
  return {
    id: null,
    question: "",
    image: "",
    imagePreview: "",
    audio: "",
    audioPreview: "",
    options: Array.from({ length: cfg.optionCount }, () => ""),
    correctIndex: 0,
    uploadingImage: false,
    uploadingAudio: false,
  };
}

function questionFromServer(q, stageType) {
  const cfg = STAGE_TYPE_BY_VALUE[stageType];
  const options = [q.option_one, q.option_two, q.option_three, q.option_four]
    .slice(0, cfg.optionCount)
    .map((o) => String(o || ""));
  let correctIndex = options.findIndex(
    (o) => o.trim().toLowerCase() === String(q.correct_answer || "").trim().toLowerCase()
  );
  if (correctIndex < 0) correctIndex = 0;
  return {
    id: q.id,
    question: q.question || "",
    image: q.image || "",
    imagePreview: s3Url(q.image),
    audio: q.audio || "",
    audioPreview: s3Url(q.audio),
    options,
    correctIndex,
    uploadingImage: false,
    uploadingAudio: false,
  };
}

function defaultQuestionCards(stageType) {
  return Array.from({ length: MIN_QUESTION_CARDS }, () => emptyQuestionCard(stageType));
}

const StageQuiz = () => {
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme } = useSelector((state) => state.theme);

  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const [stageDraft, setStageDraft] = useState(null); // { id, stage_type, title, heading }
  const [questionCards, setQuestionCards] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileInputs = useRef({});
  const tour = useAdminTour("stagequiz", navigate);

  const loadStages = useCallback(async () => {
    if (!currentTheme || !adminToken) return;
    setLoading(true);
    try {
      const data = await getQuizStages({ currentTheme, organizationId, sessionId, token: adminToken });
      setStages(data.stages || []);
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to load stages", "error");
    } finally {
      setLoading(false);
    }
  }, [currentTheme, adminToken, organizationId, sessionId]);

  useEffect(() => {
    loadStages();
  }, [loadStages]);

  // Reset the working draft whenever the active tab or the underlying stage list changes.
  useEffect(() => {
    const stage = stages[activeIndex];
    if (!stage) return;
    setStageDraft({ id: stage.id, stage_type: stage.stage_type, title: stage.title, heading: stage.heading });
    setQuestionCards(
      stage.questions && stage.questions.length
        ? stage.questions.map((q) => questionFromServer(q, stage.stage_type))
        : defaultQuestionCards(stage.stage_type)
    );
  }, [stages, activeIndex]);

  const handleTypeChange = (newType) => {
    setStageDraft((d) => ({ ...d, stage_type: newType }));
    // Switching back to this stage's saved type restores its saved questions
    // instead of discarding them — a type change is just a view, not a delete.
    const original = stages[activeIndex];
    if (original && original.stage_type === newType && original.questions?.length) {
      setQuestionCards(original.questions.map((q) => questionFromServer(q, newType)));
    } else {
      setQuestionCards(defaultQuestionCards(newType));
    }
  };

  const updateCard = (idx, patch) => {
    setQuestionCards((prev) => prev.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  };

  const addQuestionCard = () => {
    if (questionCards.length >= MAX_QUESTION_CARDS) return;
    setQuestionCards((prev) => [...prev, emptyQuestionCard(stageDraft.stage_type)]);
  };

  const removeQuestionCard = (idx) => {
    setQuestionCards((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleImageSelect = async (idx, file) => {
    if (!file) return;
    const cfg = STAGE_TYPE_BY_VALUE[stageDraft.stage_type];
    const ext = file.name.split(".").pop().toLowerCase();
    if (!cfg.imageExts.includes(ext)) {
      Swal.fire("Invalid file", `Only ${cfg.imageExts.join(", ").toUpperCase()} allowed`, "error");
      return;
    }
    if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
      Swal.fire("Invalid file", `File size exceeds ${MAX_IMAGE_MB}MB limit`, "error");
      return;
    }
    try {
      const { width, height } = await getImageDimensions(file);
      const matches = cfg.imageDimensions.some(([w, h]) => width === w && height === h);
      if (!matches) {
        Swal.fire(
          "Invalid image",
          `Image must be exactly ${dimensionsLabel(cfg.imageDimensions)} pixels`,
          "error"
        );
        return;
      }
    } catch {
      Swal.fire("Invalid file", "Could not read this file as an image", "error");
      return;
    }
    updateCard(idx, { imagePreview: URL.createObjectURL(file), uploadingImage: true });
    try {
      const key = await uploadFileDirectToS3({
        adminToken,
        organizationId,
        sessionId,
        currentTheme,
        kind: "stage_image",
        file,
      });
      updateCard(idx, { image: key, uploadingImage: false });
    } catch (e) {
      Swal.fire("Error", e.message || "Upload failed", "error");
      updateCard(idx, { uploadingImage: false });
    }
  };

  const handleAudioSelect = async (idx, file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!AUDIO_EXTS.includes(ext)) {
      Swal.fire("Invalid file", `Only ${AUDIO_EXTS.join(", ").toUpperCase()} allowed`, "error");
      return;
    }
    if (file.size > MAX_AUDIO_MB * 1024 * 1024) {
      Swal.fire("Invalid file", `File size exceeds ${MAX_AUDIO_MB}MB limit`, "error");
      return;
    }
    updateCard(idx, { audioPreview: URL.createObjectURL(file), uploadingAudio: true });
    try {
      const key = await uploadFileDirectToS3({
        adminToken,
        organizationId,
        sessionId,
        currentTheme,
        kind: "stage_audio",
        file,
      });
      updateCard(idx, { audio: key, uploadingAudio: false });
    } catch (e) {
      Swal.fire("Error", e.message || "Upload failed", "error");
      updateCard(idx, { uploadingAudio: false });
    }
  };

  const validateQuestionCards = (stageType, cards) => {
    const cfg = STAGE_TYPE_BY_VALUE[stageType];
    if (!cards.length) return "At least one question is required";
    for (let i = 0; i < cards.length; i++) {
      const c = cards[i];
      const qNo = i + 1;
      if (cfg.needsQuestionText) {
        if (!c.question.trim()) return `Question ${qNo}: question text is required`;
        if (c.question.trim().length > cfg.questionMaxLen) {
          return `Question ${qNo}: question text must not exceed ${cfg.questionMaxLen} characters`;
        }
      }
      if (cfg.needsImage && !c.image) {
        return `Question ${qNo}: ${cfg.value === "gif" ? "a GIF" : "an image"} is required`;
      }
      if (cfg.needsAudio && !c.audio) return `Question ${qNo}: an audio file is required`;
      if (c.uploadingImage || c.uploadingAudio) return `Question ${qNo}: still uploading, please wait`;
      const trimmedOptions = c.options.map((o) => o.trim());
      if (trimmedOptions.some((o) => !o)) return `Question ${qNo}: all ${cfg.optionCount} options are required`;
      if (trimmedOptions.some((o) => o.length > MAX_OPTION_LEN)) {
        return `Question ${qNo}: options must not exceed ${MAX_OPTION_LEN} characters`;
      }
      const lower = new Set(trimmedOptions.map((o) => o.toLowerCase()));
      if (lower.size !== trimmedOptions.length) return `Question ${qNo}: options must be unique`;
    }
    return null;
  };

  // ── Save (title + heading + type, then questions) — one action, matching the
  // PHP reference's single "Update" submit ──────────────────────────────────
  const handleSaveStage = async () => {
    const title = stageDraft.title.trim();
    const heading = stageDraft.heading.trim();
    if (!title) {
      Swal.fire("Validation", "Stage title is required", "warning");
      return;
    }
    if (title.length > MAX_TITLE_LEN) {
      Swal.fire("Validation", `Title must not exceed ${MAX_TITLE_LEN} characters`, "warning");
      return;
    }
    if (heading.length > MAX_HEADING_LEN) {
      Swal.fire("Validation", `Heading must not exceed ${MAX_HEADING_LEN} characters`, "warning");
      return;
    }
    const qError = validateQuestionCards(stageDraft.stage_type, questionCards);
    if (qError) {
      Swal.fire("Validation", qError, "warning");
      return;
    }

    const original = stages[activeIndex];

    setSaving(true);
    try {
      await saveQuizStages({
        currentTheme,
        organizationId,
        sessionId,
        token: adminToken,
        stages: [
          {
            id: stageDraft.id,
            stage_type: stageDraft.stage_type,
            title,
            heading,
            sort_order: original?.sort_order ?? activeIndex,
          },
        ],
      });

      const payload = questionCards.map((c, i) => ({
        id: c.id,
        sort_order: i,
        question: c.question.trim(),
        image: c.image,
        audio: c.audio,
        option_one: c.options[0]?.trim() || "",
        option_two: c.options[1]?.trim() || "",
        option_three: c.options[2]?.trim() || "",
        option_four: c.options[3] != null ? c.options[3].trim() : undefined,
        correct_answer: (c.options[c.correctIndex] || "").trim(),
      }));
      await saveQuizStageQuestions({
        currentTheme,
        organizationId,
        sessionId,
        token: adminToken,
        stageId: stageDraft.id,
        questions: payload,
      });

      await loadStages();
      Swal.fire({ icon: "success", title: "Data Updated", timer: 1200, showConfirmButton: false });
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save stage", "error");
    } finally {
      setSaving(false);
    }
  };

  // ── Reorder — swap this stage with a neighboring position ──────────────────
  const moveStage = async (direction) => {
    const target = activeIndex + direction;
    if (target < 0 || target >= stages.length) return;
    const next = [...stages];
    [next[activeIndex], next[target]] = [next[target], next[activeIndex]];
    setStages(next);
    setActiveIndex(target);
    try {
      await saveQuizStages({
        currentTheme,
        organizationId,
        sessionId,
        token: adminToken,
        stages: next.map((s, i) => ({
          id: s.id,
          stage_type: s.stage_type,
          title: s.title,
          heading: s.heading,
          sort_order: i,
        })),
      });
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save stage order", "error");
      await loadStages();
    }
  };

  const activeStage = stages[activeIndex];
  const activeCfg = stageDraft ? STAGE_TYPE_BY_VALUE[stageDraft.stage_type] : null;

  return (
    <div className="themeupdate-page">
      <div className="back-button-holder">
        <button type="button" className="back-button" onClick={() => navigate("/admin/rules")}>
          <i className="fa-solid fa-arrow-left"></i> Back
        </button>
        <button
          type="button"
          className="themeupdate-tour-btn"
          onClick={() => {
            tour.start();
            navigate("/admin/rules");
          }}
        >
          <i className="fa-solid fa-route"></i> Take a Tour
        </button>
      </div>

      <div className="stagequiz-tabbar-wrap">
        {loading && stages.length === 0 ? (
          <span className="themeupdate-hint">Loading stages...</span>
        ) : (
          <div className="stagequiz-tabbar" data-tour="stagequiz-tabs">
            {stages.map((stage, index) => (
              <button
                key={stage.id}
                type="button"
                className={`stagequiz-tabbar-btn ${index === activeIndex ? "active" : ""}`}
                onClick={() => setActiveIndex(index)}
              >
                Stage {index + 1}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="themeupdate-panel-wide">
        <div className="themeupdate-card">
          {stageDraft && activeStage && (
            <>
              <div className="themeupdate-form-section">
                <div className="stagequiz-stage-header">
                  <h4 className="themeupdate-label">Add Stage {activeIndex + 1} Title:</h4>
                  <div className="stagequiz-move-btns">
                    <button
                      type="button"
                      className="themeupdate-btn-icon"
                      disabled={activeIndex === 0}
                      onClick={() => moveStage(-1)}
                      title="Move this stage earlier"
                    >
                      <i className="fa-solid fa-arrow-left"></i>
                    </button>
                    <button
                      type="button"
                      className="themeupdate-btn-icon"
                      disabled={activeIndex === stages.length - 1}
                      onClick={() => moveStage(1)}
                      title="Move this stage later"
                    >
                      <i className="fa-solid fa-arrow-right"></i>
                    </button>
                  </div>
                </div>
                <textarea
                  className="themeupdate-textarea"
                  data-tour="stagequiz-title"
                  rows={3}
                  maxLength={MAX_TITLE_LEN}
                  value={stageDraft.title}
                  onChange={(e) => setStageDraft((d) => ({ ...d, title: e.target.value }))}
                />
                <p className="themeupdate-hint" style={{ textAlign: "right" }}>
                  (maximum {MAX_TITLE_LEN} characters)
                </p>
                <p className="themeupdate-muted">
                  <code>* This title will reflect on the game stage page</code>
                </p>

                <h4 className="themeupdate-label" style={{ marginTop: 16 }}>
                  Question Heading
                </h4>
                <input
                  type="text"
                  className="themeupdate-input"
                  data-tour="stagequiz-heading"
                  maxLength={MAX_HEADING_LEN}
                  placeholder="Shown above this stage's questions"
                  value={stageDraft.heading}
                  onChange={(e) => setStageDraft((d) => ({ ...d, heading: e.target.value }))}
                />
                <p className="themeupdate-hint" style={{ textAlign: "right" }}>
                  (maximum {MAX_HEADING_LEN} characters)
                </p>

                <h4 className="themeupdate-label" style={{ marginTop: 16 }}>
                  Question Type
                </h4>
                <select
                  className="themeupdate-select"
                  data-tour="stagequiz-type"
                  value={stageDraft.stage_type}
                  onChange={(e) => handleTypeChange(e.target.value)}
                >
                  {STAGE_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label} — {t.description}
                    </option>
                  ))}
                </select>
                <p className="themeupdate-muted">
                  <code>* Choosing a type shows only the upload/options fields that type needs below</code>
                </p>
              </div>

              <div className="themeupdate-table-section">
                <h4 className="themeupdate-label">Game Settings — Add Questions:</h4>
                {activeCfg.needsImage && (
                  <p className="themeupdate-muted">
                    <code>
                      * Allowed {activeCfg.value === "gif" ? "GIF" : "Photo"} Formats -{" "}
                      {activeCfg.imageExts.join(", ").toUpperCase()}
                    </code>
                  </p>
                )}
                {activeCfg.needsImage && (
                  <p className="themeupdate-muted">
                    <code>
                      * Required Dimensions: {dimensionsLabel(activeCfg.imageDimensions)}
                    </code>
                  </p>
                )}
                {activeCfg.needsImage && (
                  <p className="themeupdate-muted">
                    <code>* Required Size less than {MAX_IMAGE_MB}Mb</code>
                  </p>
                )}
                {activeCfg.needsAudio && (
                  <p className="themeupdate-muted">
                    <code>
                      * Allowed Audio Formats - {AUDIO_EXTS.join(", ").toUpperCase()}, less than {MAX_AUDIO_MB}Mb
                    </code>
                  </p>
                )}
                {!activeCfg.needsImage && !activeCfg.needsAudio && (
                  <p className="themeupdate-muted">
                    <code>* Text question — no file upload needed, {activeCfg.questionMaxLen} characters max</code>
                  </p>
                )}
                <p className="themeupdate-muted">
                  <code>* Options max {MAX_OPTION_LEN} characters</code>
                </p>

                <div className="stagequiz-question-grid" data-tour="stagequiz-questions">
                  {questionCards.map((card, idx) => (
                    <div className="stagequiz-question-card" key={idx}>
                      <button
                        type="button"
                        className="stagequiz-card-remove"
                        onClick={() => removeQuestionCard(idx)}
                        title="Remove question"
                      >
                        <i className="fa-solid fa-times"></i>
                      </button>

                      <div className="stagequiz-card-label">Question {idx + 1}</div>

                      {activeCfg.needsImage && (
                        <div className="stagequiz-file-row">
                          <input
                            ref={(el) => (fileInputs.current[`img-${idx}`] = el)}
                            type="file"
                            accept={activeCfg.imageAccept}
                            onChange={(e) => handleImageSelect(idx, e.target.files?.[0])}
                          />
                          {card.uploadingImage && <span className="stagequiz-uploading">Uploading…</span>}
                        </div>
                      )}

                      {activeCfg.needsAudio && (
                        <div className="stagequiz-file-row">
                          <input
                            ref={(el) => (fileInputs.current[`audio-${idx}`] = el)}
                            type="file"
                            accept=".mp3,.wav,.ogg,.m4a,.aac"
                            onChange={(e) => handleAudioSelect(idx, e.target.files?.[0])}
                          />
                          {card.uploadingAudio && <span className="stagequiz-uploading">Uploading…</span>}
                        </div>
                      )}

                      {activeCfg.needsQuestionText && (
                        <textarea
                          className="themeupdate-textarea stagequiz-question-text"
                          placeholder="Question text / prompt"
                          maxLength={activeCfg.questionMaxLen}
                          value={card.question}
                          onChange={(e) => updateCard(idx, { question: e.target.value })}
                        />
                      )}

                      <div className="stagequiz-options-label">Options:</div>
                      {card.options.map((opt, optIdx) => (
                        <label className="stagequiz-option-row" key={optIdx}>
                          <input
                            type="radio"
                            name={`correct-${idx}`}
                            checked={card.correctIndex === optIdx}
                            onChange={() => updateCard(idx, { correctIndex: optIdx })}
                          />
                          <input
                            type="text"
                            className="themeupdate-input"
                            placeholder={`Option ${optIdx + 1}`}
                            maxLength={MAX_OPTION_LEN}
                            value={opt}
                            onChange={(e) => {
                              const next = [...card.options];
                              next[optIdx] = e.target.value;
                              updateCard(idx, { options: next });
                            }}
                          />
                        </label>
                      ))}

                      {card.imagePreview && (
                        <div className="stagequiz-media-preview">
                          <img src={card.imagePreview} alt="Question" />
                        </div>
                      )}
                      {card.audioPreview && (
                        <div className="stagequiz-media-preview">
                          <audio controls src={card.audioPreview}></audio>
                        </div>
                      )}
                    </div>
                  ))}

                  {questionCards.length < MAX_QUESTION_CARDS && (
                    <button type="button" className="stagequiz-add-card" onClick={addQuestionCard}>
                      <i className="fa-solid fa-plus"></i>
                      <span>Add Question</span>
                    </button>
                  )}
                </div>

                <div className="themeupdate-modal-actions-row stagequiz-save-row" data-tour="stagequiz-save">
                  <button
                    type="button"
                    className="themeupdate-btn themeupdate-btn-primary"
                    disabled={saving}
                    onClick={handleSaveStage}
                  >
                    <i className="fa-solid fa-cloud-arrow-up"></i> {saving ? "Saving..." : "Update"}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <TourOverlay
        step={tour.step}
        stepNumber={tour.stepNumber}
        totalSteps={tour.totalSteps}
        isLast={tour.isLast}
        onNext={tour.goNext}
        onPrev={tour.goPrev}
        onSkip={tour.stop}
      />
    </div>
  );
};

export default StageQuiz;
